import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import {
  Box,
  Typography,
  Alert,
  Paper,
  Button,
  TextField,
  Grid
} from "@mui/material";
import api from "../../services/api";
import HorseLoader from "../../components/loadingHorse";
import { useCryptoNavigation } from "../../App";

export default function TenantTypeForm() {
  const { t } = useTranslation()
  const { getParam, navigateToPage } = useCryptoNavigation();
  
  // Obter parâmetros da navegação criptografada
  const mode = getParam('mode') || 'new';
  const id = getParam('id');
  const isEditMode = mode === 'edit' && !!id;
  
  // Interface para os dados do formulário
  interface TenantTypeFormData {
    tenantTypeName: string;
    [key: string]: any;
  }

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TenantTypeFormData>({ tenantTypeName: '' });
  
  // Debug logs
  useEffect(() => {
    console.log('🏠 TenantTypeForm montado');
    console.log('📊 Parâmetros:', { mode, id, isEditMode });
  }, [mode, id, isEditMode]);

  // Carregar dados iniciais se estiver em modo de edição
  useEffect(() => {
    if (isEditMode && id) {
      const fetchTenantType = async () => {
        try {
          setLoading(true);
          setError(null);
          console.log(`🔍 Buscando dados do tipo de tenant com ID: ${id}`);
          
          // Tentar diferentes endpoints
          try {
            const response = await api.get(`/tenant_type/${id}`);
            console.log("📦 Resposta da API:", response.data);
            
            // Verificar estrutura da resposta
            let typeData;
            if (response.data && response.data.tenantType) {
              typeData = response.data.tenantType;
            } else if (Array.isArray(response.data) && response.data.length > 0) {
              typeData = response.data[0];
            } else {
              typeData = response.data;
            }
            
            // Atualizar o estado com os dados recebidos
            setFormData({
              tenantTypeName: typeData.tenantTypeName || typeData.name || ""
            });
            
            console.log("✅ Dados carregados:", typeData);
          } catch (err) {
            console.warn("⚠️ Primeira tentativa falhou, tentando rota alternativa");
            // Tentar rota alternativa
            const alternativeResponse = await api.get(`/tenant_type`);
            
            if (Array.isArray(alternativeResponse.data)) {
              // Encontrar o item pelo ID
              const foundItem = alternativeResponse.data.find((item: any) => 
                (item.tenantTypeId === Number(id) || item.id === Number(id))
              );
              
              if (foundItem) {
                setFormData({
                  tenantTypeName: foundItem.tenantTypeName || foundItem.name || ""
                });
                console.log("✅ Dados encontrados na lista:", foundItem);
              } else {
                throw new Error(t("tenantTypes.itemNotFound"));
              }
            } else {
              throw new Error("Formato de resposta inesperado");
            }
          }
        } catch (err: any) {
          console.error("❌ Erro ao carregar tipo de tenant:", err);
          setError(t("tenantTypes.loadError", { error: err.message }));
        } finally {
          setLoading(false);
        }
      };

      fetchTenantType();
    }
  }, [isEditMode, id]);

  // Função para transformar dados antes de salvar
  const transformBeforeSave = (data: any) => {
    console.log("🔄 Transformando dados para salvar:", data);
    // Manter o ID se estiver em modo de edição
    return {
      ...(isEditMode ? { tenantTypeId: parseInt(id as string) } : {}),
      tenantTypeName: data.tenantTypeName
    };
  };

  // Função para lidar com o envio do formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.tenantTypeName.trim()) {
      setError(t("tenantTypes.nameRequired"));
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const dataToSave = transformBeforeSave(formData);
      console.log("💾 Salvando dados:", dataToSave);
      
      if (isEditMode) {
        await api.put(`/tenant_type/${id}`, dataToSave);
        console.log("✅ Tipo de proprietário atualizado com sucesso");
      } else {
        await api.post(`/tenant_type`, dataToSave);
        console.log("✅ Tipo de proprietário criado com sucesso");
      }
      
      // Usar navegação criptografada para voltar à lista
      console.log("🏠 Retornando à lista de tipos de proprietário");
      navigateToPage('tipos', { mode: 'list' });
      
    } catch (err: any) {
      console.error("❌ Erro ao salvar:", err);
      const errorMessage = err.response?.data?.message || err.message || t("tenantTypes.unknownError");
      setError(t("tenantTypes.saveError", { error: errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  // Função para lidar com mudanças nos campos
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: TenantTypeFormData) => ({ 
      ...prev, 
      [name]: value 
    }));
    
    // Limpar erro quando usuário começar a digitar
    if (error && value.trim()) {
      setError(null);
    }
  };

  // Função para cancelar e voltar à lista
  const handleCancel = () => {
    console.log("❌ Cancelando e voltando à lista");
    navigateToPage('tipos', { mode: 'list' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <HorseLoader />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {isEditMode ? t("tenantTypes.editTitle") : t("tenantTypes.newTitle")}
      </Typography>
      
      {/* Informações de debug em desenvolvimento
      {process.env.NODE_ENV === 'development' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Debug: Modo = {mode}, ID = {id || 'N/A'}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )} */}
      
      <form onSubmit={handleSubmit}>
        {/* <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            component="label" 
            htmlFor="tenantTypeName" 
            sx={{ mb: 1, display: 'block', fontWeight: 'medium' }}
          >
            Nome do Tipo 
          </Typography>
          <input
            type="text"
            id="tenantTypeName"
            name="tenantTypeName"
            value={formData.tenantTypeName || ''}
            onChange={handleChange}
            required
            disabled={loading}
            placeholder={t("tenantTypes.namePlaceholder")}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '4px',
              border: `1px solid ${error ? '#f44336' : '#ccc'}`,
              fontSize: '16px',
              fontFamily: 'inherit',
              backgroundColor: loading ? '#f5f5f5' : '#fff',
              cursor: loading ? 'not-allowed' : 'text'
            }}
          /> */}
          <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("tenants.typeLabel")}
                name="tenantTypeName"
                value={formData.tenantTypeName || ''}
                onChange={handleChange}
                required
                // error={!!errors.tenantTypeName}
                // helperText={errors.tenantName}
                margin="normal"
              />
            </Grid>
            </Grid>
            </Box>
        {/* </Box> */}
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button 
            onClick={handleCancel}
            variant="outlined" 
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            {t("common.cancel")}
          </Button>
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading || !formData.tenantTypeName.trim()}
            sx={{ minWidth: 100 }}
          >
            {loading ? t("common.saving") : (isEditMode ? t("common.update") : t("common.save"))}
          </Button>
        </Box>
      </form>
    </Paper>
  );
}